import { isString } from '../shared/runtime-values.js';
import { LANDMARK_MARKERS, landmarkMarker, landmarkImage } from './landmark-markers.js';
import {
  Bed, Bike, Camera, Car, CircleAlert, CircleParking, Coffee, Ellipsis, Flag, Fuel,
  Heart, Hospital, House, Info, Landmark, MapPin, Mountain, Plane, Ship, ShoppingCart,
  Star, TentTree, Trees, Utensils, Waves, createElement, type IconNode,
} from 'lucide';
import { type MarkerIconName } from '../shared/marker-icon.js';

export const PRIMARY_MARKER_ICON_OPTIONS = [
  { name: 'map-pin', label: 'Pin', icon: MapPin },
  { name: 'star', label: 'Star', icon: Star },
  { name: 'flag', label: 'Flag', icon: Flag },
  { name: 'camera', label: 'Camera', icon: Camera },
  { name: 'heart', label: 'Heart', icon: Heart },
  { name: 'info', label: 'Information', icon: Info },
  { name: 'circle-alert', label: 'Alert', icon: CircleAlert },
  { name: 'fuel', label: 'Fuel', icon: Fuel },
  { name: 'trees', label: 'Trees', icon: Trees },
] as const satisfies ReadonlyArray<{ name: string; label: string; icon: IconNode }>;

export const MORE_MARKER_ICON_OPTIONS = [
  { name: 'house', label: 'Home', icon: House },
  { name: 'hospital', label: 'Hospital', icon: Hospital },
  { name: 'shopping-cart', label: 'Shopping', icon: ShoppingCart },
  { name: 'utensils', label: 'Restaurant', icon: Utensils },
  { name: 'coffee', label: 'Cafe', icon: Coffee },
  { name: 'bed', label: 'Lodging', icon: Bed },
  { name: 'plane', label: 'Airport', icon: Plane },
  { name: 'car', label: 'Car', icon: Car },
  { name: 'circle-parking', label: 'Parking', icon: CircleParking },
  { name: 'ship', label: 'Port', icon: Ship },
  { name: 'bike', label: 'Cycling', icon: Bike },
  { name: 'mountain', label: 'Mountain', icon: Mountain },
  { name: 'waves', label: 'Water', icon: Waves },
  { name: 'tent-tree', label: 'Camping', icon: TentTree },
  { name: 'landmark', label: 'Landmark', icon: Landmark },
  ...LANDMARK_MARKERS.map(marker => ({ ...marker, icon: Landmark })),
] as const satisfies ReadonlyArray<{ name: string; label: string; icon: IconNode }>;

export const MARKER_ICON_OPTIONS = [
  ...PRIMARY_MARKER_ICON_OPTIONS,
  ...MORE_MARKER_ICON_OPTIONS,
  { name: 'ellipsis', label: 'More', icon: Ellipsis },
] as const satisfies ReadonlyArray<{ name: string; label: string; icon: IconNode }>;

export function markerIconSvg(name: MarkerIconName): string {
  const landmark = landmarkMarker(name);
  if (landmark) return `<svg width="22" height="22" viewBox="0 0 160 160" aria-hidden="true"><image href="${landmark.src}" width="160" height="160"/></svg>`;
  const option = getMarkerIconOption(name);
  return createElement(option.icon, { width: 22, height: 22, 'aria-hidden': 'true' }).outerHTML;
}

/** Draw the same Lucide definition used by the marker picker onto a Canvas. */
export function drawMarkerIcon(ctx: CanvasRenderingContext2D, name: MarkerIconName, x: number, y: number, size: number, color: string) {
  if (landmarkMarker(name)) {
    const image = landmarkImage(name);
    if (image) ctx.drawImage(image, x, y, size, size);
    return;
  }
  const { icon } = getMarkerIconOption(name);
  const scale = size / 24;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const [element, attributes] of icon) {
    if (element === 'path') {
      const pathData = attributes.d;
      if (isString(pathData)) ctx.stroke(new Path2D(pathData));
    } else if (element === 'circle') {
      ctx.beginPath();
      ctx.arc(Number(attributes.cx), Number(attributes.cy), Number(attributes.r), 0, Math.PI * 2);
      ctx.stroke();
    } else if (element === 'line') {
      ctx.beginPath();
      ctx.moveTo(Number(attributes.x1), Number(attributes.y1));
      ctx.lineTo(Number(attributes.x2), Number(attributes.y2));
      ctx.stroke();
    }
  }

  ctx.restore();
}

function getMarkerIconOption(name: MarkerIconName) {
  return MARKER_ICON_OPTIONS.find((entry) => entry.name === name) || MARKER_ICON_OPTIONS[0];
}
